import { API_URL } from '../../config';
import { getSkillById } from '../gphSkillRegistry';
import {
  validateCoachTrainingSlots,
  buildMissingCoachTrainingSlotsMessage,
  buildInvalidCoachTrainingSlotsMessage
} from '../validation/coachTrainingSlotValidator';
import {
  renderCoachTrainingResult,
  renderCoachTrainingHumanError
} from '../renderers/coachTrainingResultRenderer';

const readResponseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const extractBackendMessage = (data = {}) => {
  const detail = data.detail || data.message || data.error;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return null;
};

const toBackendGameId = (gameOrProfile) => {
  if (gameOrProfile === 'codm') return 'cod_mobile';
  if (gameOrProfile === 'profile') return null;
  return gameOrProfile || null;
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const text = query.toString();
  return text ? `?${text}` : '';
};

const coachFetch = async ({
  path,
  method = 'GET',
  body = null,
  fetchImpl,
  apiBaseUrl,
  token
}) => {
  const response = await fetchImpl(`${apiBaseUrl}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  const data = await readResponseJson(response);
  if (!response.ok) {
    const error = new Error(extractBackendMessage(data) || 'COACH_API_ERROR');
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
};

const pickProfileGameId = (progressPayload = {}) => {
  const profile = progressPayload.profile || {};
  return profile.currentGameId || profile.gameId || null;
};

const resolveCoachGameId = async ({
  slots,
  fetchImpl,
  apiBaseUrl,
  token,
  language
}) => {
  const directGameId = toBackendGameId(slots.gameOrProfile);
  if (directGameId) return { gameId: directGameId, progress: null };

  const progress = await coachFetch({
    path: `/coach/progress${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });
  const profileGameId = pickProfileGameId(progress);
  if (profileGameId) return { gameId: toBackendGameId(profileGameId) || profileGameId, progress };

  return { gameId: null, progress };
};

const fetchTrainingDetailIfAllowed = async ({
  training,
  fetchImpl,
  apiBaseUrl,
  token,
  language
}) => {
  if (!training?.id || training.locked || training.accessAllowed === false) return training;
  const detail = await coachFetch({
    path: `/coach/trainings/${encodeURIComponent(training.id)}${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });
  return detail.training || training;
};

export const prepareCoachTrainingSkill = ({ slots = {} } = {}) => {
  const skill = getSkillById('coach_training');
  const validation = validateCoachTrainingSlots(slots);

  if (validation.invalidSlots.length > 0) {
    return {
      status: 'invalid',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: validation.invalidSlots,
      message: buildInvalidCoachTrainingSlotsMessage(validation.invalidSlots)
    };
  }

  if (validation.missingSlots.length > 0) {
    return {
      status: 'needs_slots',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: [],
      message: buildMissingCoachTrainingSlotsMessage(validation.missingSlots)
    };
  }

  return {
    status: 'ready',
    skill,
    slots: validation.normalizedSlots,
    missingSlots: [],
    invalidSlots: [],
    message: null
  };
};

const recommendCoachTraining = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token, language } = options;
  const resolved = await resolveCoachGameId({ slots, fetchImpl, apiBaseUrl, token, language });
  if (!resolved.gameId) {
    return {
      status: 'needs_slots',
      slots,
      missingSlots: ['gameOrProfile'],
      invalidSlots: [],
      message: buildMissingCoachTrainingSlotsMessage(['gameOrProfile'])
    };
  }

  const query = buildQuery({
    search: slots.painOrObjective,
    limit: 6,
    locale: language,
    language
  });
  let list = await coachFetch({
    path: `/coach/games/${encodeURIComponent(resolved.gameId)}/trainings${query}`,
    fetchImpl,
    apiBaseUrl,
    token
  });

  if (!Array.isArray(list.trainings) || list.trainings.length === 0) {
    list = await coachFetch({
      path: `/coach/games/${encodeURIComponent(resolved.gameId)}/trainings${buildQuery({ limit: 6, locale: language, language })}`,
      fetchImpl,
      apiBaseUrl,
      token
    });
  }

  const training = Array.isArray(list.trainings) ? list.trainings[0] : null;
  if (!training) {
    return {
      status: 'success',
      slots,
      result: {
        kind: 'training_recommendation',
        reason: 'Nao encontrei um treino publicado para esse pedido agora.',
        training: null
      }
    };
  }

  const detailedTraining = await fetchTrainingDetailIfAllowed({
    training,
    fetchImpl,
    apiBaseUrl,
    token,
    language
  });

  return {
    status: 'success',
    slots: { ...slots, gameOrProfile: resolved.gameId },
    result: {
      kind: 'training_recommendation',
      reason: 'Escolhi um treino real da biblioteca da Coach para esse objetivo.',
      training: detailedTraining,
      access: list.access || null
    }
  };
};

const continueCoachTrack = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token, language } = options;
  const progress = await coachFetch({
    path: `/coach/progress${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });
  const firstTrack = Array.isArray(progress.recommendations?.tracks)
    ? progress.recommendations.tracks[0]
    : null;
  const firstTrainingId = firstTrack?.trainingIds?.[0] || progress.access?.firstFreeTrainingId || progress.access?.freeTrainingId;

  if (!firstTrainingId) {
    return {
      status: 'success',
      slots,
      result: {
        kind: 'progress_summary',
        ...progress
      }
    };
  }

  const detail = await coachFetch({
    path: `/coach/trainings/${encodeURIComponent(firstTrainingId)}${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });

  return {
    status: 'success',
    slots,
    result: {
      kind: 'training_detail',
      training: detail.training,
      access: detail.access || progress.access || null
    }
  };
};

const openCoachTraining = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token, language } = options;
  const detail = await coachFetch({
    path: `/coach/trainings/${encodeURIComponent(slots.trainingId)}${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });
  return {
    status: 'success',
    slots,
    result: {
      kind: 'training_detail',
      training: detail.training,
      access: detail.access || null
    }
  };
};

const completeCoachTraining = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token } = options;
  const data = await coachFetch({
    path: '/coach/progress',
    method: 'POST',
    body: {
      trainingId: slots.trainingId,
      completed: true,
      status: 'completed'
    },
    fetchImpl,
    apiBaseUrl,
    token
  });
  return {
    status: 'success',
    slots,
    result: {
      kind: 'action_confirmation',
      title: 'Treino concluido',
      message: 'Salvei esse treino como concluido na sua Coach.',
      trainingId: data.entry?.trainingId || slots.trainingId,
      entry: data.entry || null
    }
  };
};

const saveCoachTraining = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token } = options;
  const data = await coachFetch({
    path: '/coach/saved',
    method: 'POST',
    body: { trainingId: slots.trainingId },
    fetchImpl,
    apiBaseUrl,
    token
  });
  return {
    status: 'success',
    slots,
    result: {
      kind: 'action_confirmation',
      title: 'Treino salvo',
      message: 'Salvei esse treino na sua lista da Coach.',
      trainingId: data.entry?.trainingId || slots.trainingId,
      entry: data.entry || null
    }
  };
};

const showCoachProgress = async (options) => {
  const { slots, fetchImpl, apiBaseUrl, token, language } = options;
  const progress = await coachFetch({
    path: `/coach/progress${buildQuery({ locale: language, language })}`,
    fetchImpl,
    apiBaseUrl,
    token
  });
  return {
    status: 'success',
    slots,
    result: {
      kind: 'progress_summary',
      ...progress
    }
  };
};

export const executeCoachTrainingSkill = async ({
  slots = {},
  fetchImpl = globalThis.fetch,
  apiBaseUrl = API_URL,
  authToken = null,
  getAuthToken = null,
  language = 'pt-BR'
} = {}) => {
  const prepared = prepareCoachTrainingSkill({ slots });

  if (prepared.status !== 'ready') {
    return prepared;
  }

  if (typeof fetchImpl !== 'function') {
    return {
      ...prepared,
      status: 'error',
      message: 'Nao consegui conectar a Coach agora. Tenta novamente em instantes.'
    };
  }

  try {
    const token = authToken || (typeof getAuthToken === 'function' ? await getAuthToken() : null);
    const options = {
      slots: prepared.slots,
      fetchImpl,
      apiBaseUrl,
      token,
      language
    };

    const result = await ({
      recommend_training: recommendCoachTraining,
      continue_track: continueCoachTrack,
      open_training: openCoachTraining,
      complete_training: completeCoachTraining,
      save_training: saveCoachTraining,
      show_progress: showCoachProgress
    }[prepared.slots.trainingIntent])(options);

    return {
      ...prepared,
      ...result,
      renderedText: result.result ? renderCoachTrainingResult(result.result) : null
    };
  } catch (error) {
    return {
      ...prepared,
      status: 'blocked_or_error',
      backendStatus: error.status || null,
      message: renderCoachTrainingHumanError({
        status: error.status,
        message: error.message
      })
    };
  }
};

export default executeCoachTrainingSkill;
